import React, { useMemo } from 'react';
import {
  Select,
  Row,
  Col,
  DatePicker,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import { getRepeatText } from '../helpers/time';
import Colors from '../constants/Colors';

export default function ScheduleDateRepeatRedesign({
  startTime,
  value: repeatMode = null,
  onChange,
  isTemplate,
  timezone,
  allowBiweekly,
  gutter = 20,
  onRepeatEndDateChanged,
  parsedRepeatEndDate,
  style,
  isFieldDisplay,
}) {
  const startDT = useMemo(() => (
    startTime
      ? DateTime.fromMillis(startTime, { zone: timezone })
      : DateTime.local()
  ), [startTime, timezone]);

  const options = useMemo(() => {
    const opts = [
      { label: getRepeatText({ mode: null, datetime: startDT, isTemplate }), value: null },
      { label: getRepeatText({ mode: 'daily', datetime: startDT, isTemplate }), value: 'daily' },
      { label: getRepeatText({ mode: 'weekly', datetime: startDT, isTemplate }), value: 'weekly' },
      { label: getRepeatText({ mode: 'biweekly', datetime: startDT, isTemplate }), value: 'biweekly' },
      { label: getRepeatText({ mode: 'monthlyNumber', datetime: startDT, isTemplate }), value: 'monthlyNumber' },
      { label: getRepeatText({ mode: 'monthlyDay', datetime: startDT, isTemplate }), value: 'monthlyDay' },
      { label: getRepeatText({ mode: 'weekdays', datetime: startDT, isTemplate }), value: 'weekdays' },
      { label: getRepeatText({ mode: 'annually', datetime: startDT, isTemplate }), value: 'annually' },
    ];
    if (allowBiweekly) return opts;
    return opts.filter((opt) => opt.value !== 'biweekly');
  }, [startDT, isTemplate, allowBiweekly]);

  if (isFieldDisplay) {
    if (!repeatMode) return null;
    const repeatText = getRepeatText({ mode: repeatMode, datetime: startDT });
    if (!repeatText) return null;
    const repeatEnd = parsedRepeatEndDate && parsedRepeatEndDate.isValid()
      ? DateTime.fromMillis(parsedRepeatEndDate.valueOf()).toLocaleString(DateTime.DATE_MED)
      : null;

    const finalText = repeatEnd ? `${repeatText} until ${repeatEnd}` : repeatText;

    return (
      <div className="form-field-display-value-text">
        <span style={{ color: Colors.GRAY_800 }}>Repeat: </span>
        {finalText}
      </div>
    );
  }

  const textClassName = 'form-field-title';

  return (
    <>
      <Row gutter={gutter} style={style} align="bottom">
        <Col span={12}>
          <span className={textClassName}>Repeat</span>
        </Col>
        {repeatMode && (
          <Col span={12}>
            <span className={textClassName}>Repeat ends on</span>
          </Col>
        )}
      </Row>
      <Row gutter={gutter} style={{ marginTop: 10, ...style }} align="bottom">
        <Col span={12}>
          <Select
            className="form-field-select-container-repeat"
            value={repeatMode}
            onSelect={onChange}
            style={{ width: '100%' }}
            options={options}
            suffixIcon={<SyncOutlined />}
          />
        </Col>
        {repeatMode && (
          <Col span={12}>
            <DatePicker
              className="form-field-datetime-container"
              format="MMM Do YY"
              placeholder="Repeat End Date"
              onChange={onRepeatEndDateChanged}
              value={parsedRepeatEndDate}
            />
          </Col>
        )}
      </Row>
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
ScheduleDateRepeatRedesign.propTypes = {
  startTime: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
  isTemplate: PropTypes.bool,
  timezone: PropTypes.string,
  allowBiweekly: PropTypes.bool,
  gutter: PropTypes.number,
  onRepeatEndDateChanged: PropTypes.func,
  parsedRepeatEndDate: PropTypes.object,
  style: PropTypes.object,
  isFieldDisplay: PropTypes.bool,
};

ScheduleDateRepeatRedesign.defaultProps = {
  value: null,
  isFieldDisplay: false,
  parsedRepeatEndDate: null,
  startTime: null,
  isTemplate: false,
  allowBiweekly: false,
  gutter: 20,
  style: {},
  onChange: () => {},
  timezone: DateTime.local().zoneName,
  onRepeatEndDateChanged: () => {},
};
